<template>
  <div class="admin-password-reset-verification">
    <a-layout
      :style="{
        minHeight: '100vh',
        minWidth: '100%',
        background: '#fff',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, .1)',
      }"
    >
      <a-row type="flex" justify="center">
        <a-col :md="24">
          <div
            v-if="verifying"
            class="admin-password-reset-verification-verifying"
          >
            <a-spin
              :spinning="verifying"
              size="large"
              :tip="labels.verifying"
            />
          </div>
          <div
            v-else-if="has_error"
            class="admin-password-reset-verification-content"
          >
            <font-awesome-icon
              :icon="['fas', 'times-circle']"
              size="5x"
              :class="'danger'"
            />
            <p>{{ message }}</p>
            <div>
              <router-link class="btn-login" :to="{ name: 'admin' }">{{
                labels.goToLogin
              }}</router-link>
            </div>
          </div>
          <a-card
            v-else
            :style="{
              margin: '0 auto',
              borderRadius: '15px',
            }"
            class="admin-password-reset-verification-card"
            width="100%"
            :bodyStyle="{ padding: '0px' }"
          >
            <ValidationObserver ref="observer">
              <a-form
                :model="form"
                :label-width="80"
                slot-scope="{ validate }"
                @submit.prevent="validate().then(handleSubmit)"
              >
                <!-- title -->
                <a-form-item>
                  <a-row>
                    <a-col :span="24">
                      <h1 class="admin-password-reset-verification-title">
                        {{ labels.title }}
                      </h1>
                    </a-col>
                  </a-row>
                </a-form-item>

                <Input
                  v-model="form.password"
                  type="password"
                  vid="password"
                  rules="required"
                  :label="labels.form1.password.label"
                  :placeholder="labels.form1.password.placeholder"
                  size="large"
                  icon="lock"
                  iconStyle="color:rgba(0,0,0,.25)"
                  @keyup.enter.native="validate().then(handleSubmit)"
                  autocomplete="current-password"
                />

                <Input
                  v-model="form.password_confirmation"
                  type="password"
                  vid="password_confirmation"
                  rules="required"
                  :label="labels.form1.password_confirmation.label"
                  :placeholder="labels.form1.password_confirmation.placeholder"
                  size="large"
                  icon="lock"
                  iconStyle="color:rgba(0,0,0,.25)"
                  @keyup.enter.native="validate().then(handleSubmit)"
                  autocomplete="current-password"
                />

                <a-form-item>
                  <a-button
                    @click="validate().then(handleSubmit)"
                    type="primary"
                    class="admin-password-reset-verification-button"
                    size="large"
                    block
                    :loading="submitting"
                    :disabled="submitting"
                    >{{ labels.form1.submit }}</a-button
                  >
                </a-form-item>

                <a-form-item>
                  <a-button
                    type="default"
                    size="large"
                    class="back-link"
                    @click.prevent="handleBack"
                    block
                    ghost
                  >
                    {{ labels.form1.cancel }}
                  </a-button>
                </a-form-item>
              </a-form>
            </ValidationObserver>
          </a-card>
        </a-col>
      </a-row>
    </a-layout>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import Input from "../../components/core/VeeValidateForm/Input";
import labels from "@/utils/labels";
import bg from "@/assets/images/admin/login-bg.jpg";
import { mapActions } from "vuex";
import { adminPageTitle } from "../../utils/utils";
import _ from "lodash";

const initialState = {
  password: "",
  password_confirmation: "",
};

export default {
  name: "PasswordResetVerification",
  components: {
    ValidationObserver,
    Input,
  },
  metaInfo: {
    title: adminPageTitle("Recuperar contraseña"),
  },
  data() {
    return {
      labels: {
        ...labels.adminPasswordResetForm,
        appName: labels.appName,
        unknownServerError: labels.unknownServerError,
      },
      bg,
      verifying: true,
      has_error: false,
      form: _.cloneDeep(initialState),
      submitting: false,
    };
  },
  created() {
    this.verify();
  },
  methods: {
    ...mapActions("adminAuth", [
      "handlePasswordResetCodeVerify",
      "handlePasswordReset",
    ]),
    handleBack() {
      this.$router.push({ name: "admin" });
    },
    async verify() {
      try {
        this.verifying = true;
        let code = this.$route.params.code;
        if (!code) {
          this.has_error = true;
          this.message = this.labels.invalidToken;
          this.verifying = false;
          return;
        }
        await this.handlePasswordResetCodeVerify(code);
        this.has_error = false;
        this.message = "";
      } catch (error) {
        this.message =
          error?.response?.data?.message ||
          error?.response?.message ||
          error.message;
        this.has_error = true;
      } finally {
        this.verifying = false;
      }
    },
    async handleSubmit() {
      try {
        let code = this.$route.params.code;
        await this.handlePasswordReset({ ...this.form, code });
        this.$success({
          title: this.labels.form1.success,
          centered: true,
          closable: false,
          maskClosable: false,
          keyboard: false,
          class: "success-modal",
          okText: this.labels.form1.okText,
          autoFocusButton: "ok",
          onOk: () => {
            this.form = _.cloneDeep(initialState);
            this.$refs?.observer?.reset();
            this.handleBack();
          },
          onCancel: () => {
            this.form = _.cloneDeep(initialState);
            this.$refs?.observer?.reset();
            this.handleBack();
          },
        });
      } catch (error) {
        this.$error({
          title: this.labels.error,
          content:
            error?.response?.data?.message ||
            error?.response?.message ||
            error.message,
          centered: true,
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/variable";
.admin-password-reset-verification {
  font-family: "Montserrat", sans-serif;
  &-forgot-password {
    color: color(muted);
    @media screen and (max-width: $screen-mobile) {
      padding: 0;
    }
  }
  &-button {
    background-color: color(primary) !important;
    box-shadow: 0 8px 15px 0 color(primary-shadow);
    border-radius: 999px;
  }
  .ant-layout {
    background: url(../../assets/images/admin/login-bg.jpg) no-repeat !important;
    background-color: color(primary) !important;
    min-height: 100vh;
    min-width: 100%;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    @media screen and (max-width: $screen-mobile) {
      background-size: cover !important;
    }
  }
  &-card {
    width: 500px;
    padding: 20px 30px;
    background-color: color(--white);
    position: relative;
    z-index: 9;
    margin-top: 10% !important;
    // box shadow left
    box-shadow: -25px 25px 0px 0px color(primary);
    @media screen and (max-width: $screen-mobile) {
      width: 96%;
      padding: 20px;
      margin: 0 auto;
      box-shadow: none;
      margin-top: 30% !important;
    }
  }
  &-title {
    font-size: 24px;
    font-weight: 500;
    color: color(title);
    margin-bottom: 0.1rem;
    text-align: center;
    @media screen and (max-width: $screen-mobile) {
      font-size: 20px;
    }
  }
  &-verifying {
    width: 320px;
    height: 180px;
    margin: 0 auto;
    margin-top: 10%;
    background-color: whitesmoke;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: color(primary);
    font-weight: bold;
    box-shadow: -25px 25px 0px 0px color(primary);
    :deep(.ant-spin-text) {
      font-size: 1.1rem !important;
      margin-top: 0.5rem;
    }
  }
  &-content {
    width: 420px;
    height: 280px;
    margin: 0 auto;
    margin-top: 10%;
    background-color: whitesmoke;
    border-radius: 15px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: color(primary);
    font-weight: bold;
    box-shadow: -25px 25px 0px 0px color(primary);
    h1 {
      font-size: 3.5rem;
      color: color(c-hover);
      margin-bottom: 0;
    }
    p {
      margin-top: 5px;
      font-size: 1.15rem;
      margin-bottom: 0px;
    }
    div {
      width: 210px;
      height: 40px;
      display: flex;
      flex-direction: column;
      .btn-login {
        margin-top: 20px;
        background-color: color(primary);
        color: color(primary);
        box-shadow: 0 8px 15px 0 color(primary-shadow);
        border-radius: 999px;
        padding: 10px 20px;
        text-align: center;
        color: #fff;
      }
    }
  }
}
</style>
